/**
 * Calculates a data-quality tier for a job post based on field completeness.
 *
 * Fields checked (10 total):
 *   title, description, location, work_model, responsibilities,
 *   requirements_must, benefits, stipend, skills_tags, deadline_date
 *
 *  0-3 filled → baixa  |  4-6 → media  |  7+ → alta
 */

export type QualityTier = "baixa" | "media" | "alta";

export interface QualityResult {
  tier: QualityTier;
  filled: number;
  total: number;
  missing: string[];
}

const FIELD_CHECKS: { key: string; label: string; test: (j: any) => boolean }[] = [
  { key: "title", label: "Título", test: (j) => !!j.title },
  { key: "description", label: "Descrição", test: (j) => !!j.description && j.description.length > 20 },
  { key: "location", label: "Localização", test: (j) => !!j.location_city || !!j.location_state },
  { key: "work_model", label: "Modelo de trabalho", test: (j) => !!j.work_model },
  { key: "responsibilities", label: "Responsabilidades", test: (j) => Array.isArray(j.responsibilities) && j.responsibilities.length > 0 },
  { key: "requirements_must", label: "Requisitos", test: (j) => Array.isArray(j.requirements_must) && j.requirements_must.length > 0 },
  { key: "benefits", label: "Benefícios", test: (j) => Array.isArray(j.benefits) && j.benefits.length > 0 },
  { key: "stipend", label: "Bolsa/Salário", test: (j) => !!j.stipend_min || !!j.stipend_max },
  { key: "skills_tags", label: "Skills", test: (j) => Array.isArray(j.skills_tags) && j.skills_tags.length > 0 },
  { key: "deadline_date", label: "Prazo", test: (j) => !!j.deadline_date },
];

export function getJobQuality(job: any): QualityResult {
  const missing: string[] = [];
  let filled = 0;

  for (const fc of FIELD_CHECKS) {
    if (fc.test(job)) {
      filled++;
    } else {
      missing.push(fc.label);
    }
  }

  const tier: QualityTier = filled >= 7 ? "alta" : filled >= 4 ? "media" : "baixa";

  return { tier, filled, total: FIELD_CHECKS.length, missing };
}

export const QUALITY_STYLES: Record<QualityTier, { label: string; bg: string; text: string; dot: string }> = {
  baixa: { label: "Baixa", bg: "bg-red-100 dark:bg-red-950/40", text: "text-red-700 dark:text-red-300", dot: "bg-red-500" },
  media: { label: "Média", bg: "bg-amber-100 dark:bg-amber-950/40", text: "text-amber-700 dark:text-amber-300", dot: "bg-amber-500" },
  alta: { label: "Alta", bg: "bg-green-100 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", dot: "bg-green-500" },
};
